import { CabButton, CabDropdown, CabIcon, CabToggleChip, CabTooltip } from "@CabComponents/index";
import { Box, SelectChangeEvent, Typography } from "@mui/material";
import {
  DataGridProProps, GridCallbackDetails, GridColDef,
  GridFilterModel, GridRenderCellParams, GridSortModel, GridToolbarColumnsButton, GridToolbarFilterButton
} from "@mui/x-data-grid-pro";
import { IoCreateOutline } from 'react-icons/io5';
import { memo, ReactNode, useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import colors from "../../../colors";
import { PAGE_URL } from "../../../constants";
import {
  AbstractEventAnalyticsLabel, ExecutiveContact,
  ExecutiveContactType, User
} from "../../../store";
import { containsOperator, numberOperator, selectFilterOperator } from "../../../components/DataGrid/utils";
import NoRowOverlay from "../../../components/EventAnalytics/NoRowsOverlay";
import { createSelectOptions } from "../../../components/EventAnalytics/utils";
import CabDataGrid from "@CabComponents/CabDataGrid";
import { Tag } from "../../../store/cabinetApi/generated/crm";

const CREATE_NEW_TAG_VALUE = -2;


type RowData = {
  attendee: {
    id: number;
    name?: string;
    email?: string | null;
  };
  hours: number;
  tags: number[];
  contactType: number;
};

// Ensures that PDF exporting fits nicely on 1 page
const MAX_EXPORT_ROWS = 12;
const MAX_PAGE_SIZE = 12;

type ContactGridToolbarProps = {
  handleEditLabelModal: () => void;
};

const ContactGridToolbar = memo(({ handleEditLabelModal }: ContactGridToolbarProps) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "row-reverse",
      width: "100%"
    }}>
    <GridToolbarColumnsButton />
    <GridToolbarFilterButton />
    <CabButton
      icon={<CabIcon
        alt='Labels'
        size='small'
        Icon={IoCreateOutline}
      />}
      onClick={handleEditLabelModal}
      buttonType="tertiary"
      sx={{
        cursor: "pointer",
        fontSize: "13px",
        border: 0,
        paddingRight: 1,
        paddingLeft: 1,
        "&:hover": {
          border: 0
        }
      }}
    >
      Labels
    </CabButton>
  </Box>
));
ContactGridToolbar.displayName = "ContactGridToolbar";

const ContactGridToolbarSlot =
  ContactGridToolbar as unknown as NonNullable<DataGridProProps["slots"]>["toolbar"];

const ContactGridSpacerToolbar = memo(() => <Box
  sx={{
    height: "16px",
    width: "100%"
  }} />);
ContactGridSpacerToolbar.displayName = "ContactGridSpacerToolbar";

const ContactGridSpacerToolbarSlot =
  ContactGridSpacerToolbar as unknown as NonNullable<DataGridProProps["slots"]>["toolbar"];

export type ContactDataGridProps = {
  rows: RowData[];
  executive_contacts: { [key: string]: ExecutiveContact };
  user: User | undefined | null;
  executive_contact_tags: { [key: string]: Tag };
  contact_types: { [key: string]: ExecutiveContactType };
  handleUpdateExecutiveContact: (data: Partial<ExecutiveContact> & { id: number }) => void;
  tz?: string;
  editContact: (attendeeId: number) => void;
  loading: boolean;
  handleSortModelChange: (sortModel: GridSortModel) => void;
  handleFilterModelChange: (model: GridFilterModel, details: GridCallbackDetails) => void;
  handleEditLabelModal: () => void;
  renderForExport: boolean;
};

export const ContactDataGrid = ({
  rows, executive_contacts, executive_contact_tags, user,
  handleUpdateExecutiveContact, tz, editContact, loading, handleSortModelChange,
  handleFilterModelChange, contact_types, handleEditLabelModal, renderForExport
}: ContactDataGridProps) => {
  const navigate = useNavigate();

  const updateForeignKey = useCallback((
    e: SelectChangeEvent<number>,
    id: number,
    attribute: "contact_type"
  ) => {
    let value = e.target?.value ? Number(e.target?.value) : null;
    value = value === -1 ? null : value;
    handleUpdateExecutiveContact(
      { id: id, [attribute]: value }
    );
  }, [handleUpdateExecutiveContact]);

  const updateTags = useCallback((
    tags: number[],
    id: number
  ) => {
    handleUpdateExecutiveContact(
      { id: id, executive_tags: tags } as Partial<ExecutiveContact> & { id: number; executive_tags: number[] }
    );
  }, [handleUpdateExecutiveContact]);

  const renderContactName = useCallback((
    { row: { attendee } }: GridRenderCellParams<RowData, ReactNode>
  ): ReactNode => {
    const disabled = !(user?.features.CALENDAR_ANALYTICS && user?.permissions.ANALYTICS_LABELS);
    const contact_name = attendee?.name ? attendee.name : attendee?.email;
    const toolTipText = attendee?.name ? `${attendee.name} - ${attendee.email}` : attendee?.email;
    return (
      <Box
        sx={{
          display: "flex",
          flexWrap: "nowrap",
          width: "100%",
          justifyContent: "space-between",
          alignItems: "center"
        }}>
        <CabTooltip title={toolTipText} wrapWithSpan>
          <Typography variant="body2" sx={{ textOverflow: "ellipsis", overflow: "hidden", maxWidth: 230 }}>
            {contact_name}
          </Typography>
        </CabTooltip>
        {!disabled && !renderForExport &&
          <CabIcon 
            Icon={IoCreateOutline}
            onClick={() => editContact(attendee?.id)}
          />
        }
      </Box>
    );
  }, [
    editContact,
    user?.features.CALENDAR_ANALYTICS,
    user?.permissions.ANALYTICS_LABELS,
    renderForExport
  ]);

  const renderTags = useCallback((
    { row: { attendee, tags: tagIds } }: GridRenderCellParams<RowData, ReactNode>
  ): ReactNode => {
    const options = createSelectOptions(
      Object.values(executive_contact_tags),
      user?.permissions.ANALYTICS_LABELS,
      false
    );
    const disabled = !(user?.features.CALENDAR_ANALYTICS && user?.permissions.ANALYTICS_LABELS);
    const selectedTags = tagIds
      .map(tagId => executive_contact_tags[String(tagId)])
      .filter((tag): tag is Tag => !!tag);
    
    if (disabled) {
      if (selectedTags.length === 0) {
        return <CabToggleChip 
          chipColor={colors.white800} 
          label="None" 
          selected={true} 
          renderForExport={renderForExport}
        />;
      }
      return (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5
          }}>
          {selectedTags.map(tag => (
            <CabToggleChip 
              key={tag.id}
              chipColor={tag.color} 
              label={tag.name} 
              selected={true} 
              renderForExport={renderForExport}
            />
          ))}
        </Box>
      );
    } else {
      return (
        <CabDropdown<number>
          multiple
          options={options}
          value={tagIds.length > 0 ? tagIds : []}
          onChange={(e) => {
            const value = (Array.isArray(e.target.value) ? e.target.value : []) as number[];
            if (value.includes(CREATE_NEW_TAG_VALUE)) {
              navigate(`${PAGE_URL.CRM_SETTINGS}/org`);
              return;
            }
            const selectedTagIds = value.map(v => Number(v)).filter(v => !isNaN(v) && v !== -1);
            updateTags(selectedTagIds, attendee.id);
          }}
          overrides={{
            renderValue: (value: unknown) => {
              const selectedTagIds = Array.isArray(value) ? value : [];
              if (selectedTagIds.length === 0) {
                return <CabToggleChip 
                  chipColor={colors.white800} 
                  label="None" 
                  selected={true} 
                  renderForExport={renderForExport}
                />;
              }
              return (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.5
                  }}>
                  {selectedTagIds.map((tagId: number) => {
                    const tag = executive_contact_tags[String(tagId)];
                    if (!tag) return null;
                    return <CabToggleChip 
                      key={tag.id}
                      chipColor={tag.color} 
                      label={tag.name} 
                      selected={true} 
                      renderForExport={renderForExport}
                    />;
                  })}
                </Box>
              );
            },
            sx: {
              width: "100%",
              "& .MuiOutlinedInput-notchedOutline": {
                border: 0
              },
            }
          }}
          placeholder="Select tags..."
        />
      );
    }
  }, [
    executive_contact_tags,
    navigate,
    updateTags,
    user?.features.CALENDAR_ANALYTICS,
    user?.permissions.ANALYTICS_LABELS,
    renderForExport
  ]);

  const renderType = useCallback((
    { row: { attendee, contactType } }: GridRenderCellParams<RowData, ReactNode>
  ): ReactNode => {
    const options = createSelectOptions(Object.values(contact_types), user?.permissions.ANALYTICS_LABELS, true);
    const category = contact_types[contactType];

    return <CabDropdown<number>
      options={options}
      value={category?.id || -1}
      onChange={(e) => updateForeignKey(e, attendee.id, "contact_type")}
      overrides={{
        renderValue: (value: unknown) => {
          const eventId = value as number;
          const eventTypeObject = eventId > 0 ?
            contact_types[eventId] : { id: -1, name: "None", color: colors.white800 };
          return <CabToggleChip 
            chipColor={eventTypeObject.color} 
            label={eventTypeObject.name} 
            selected={true} 
            renderForExport={renderForExport}
          />;
        },
        sx: {
          width: "100%",
          "& .MuiOutlinedInput-notchedOutline": {
            border: 0
          },
        }
      }}
    />;
  }, [contact_types, user?.permissions.ANALYTICS_LABELS, updateForeignKey, renderForExport]);

  const filteringEnabled = true;

  const executiveContactTagOptions = useMemo(
    () => createSelectOptions(
      Object.values(executive_contact_tags), user?.permissions.ANALYTICS_LABELS, true),
    [executive_contact_tags, user?.permissions.ANALYTICS_LABELS]
  );

  const executiveContactTypeOptions = useMemo(
    () => createSelectOptions(
      Object.values(contact_types), user?.permissions.ANALYTICS_LABELS, true),
    [contact_types, user?.permissions.ANALYTICS_LABELS]
  );

  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'contact_lookup',
      headerName: 'Contact',
      width: 280,
      flex: 1,
      editable: false,
      display: "flex",
      filterable: filteringEnabled,
      sortable: filteringEnabled,
      renderCell: renderContactName,
      filterOperators: containsOperator,
      headerAlign: 'left'
    },
    {
      field: 'hours',
      headerName: 'Hours',
      type: 'number',
      width: 80,
      editable: false,
      display: "flex",
      filterable: filteringEnabled,
      sortable: filteringEnabled,
      align: 'right',
      headerAlign: 'center',
      valueFormatter: (value, row, column, apiRef) => Math.round(value * 10) / 10,
      filterOperators: numberOperator([])
    },
    {
      field: 'tags',
      headerName: 'Tags',
      width: renderForExport ? 240 : 140,
      editable: false,
      filterable: filteringEnabled,
      sortable: filteringEnabled,
      display: "flex",
      renderCell: renderTags,
      headerAlign: 'left',
      filterOperators: selectFilterOperator([
        {
          options: executiveContactTagOptions,
          rawOptions: executive_contact_tags as Record<string, AbstractEventAnalyticsLabel>
        }
      ]).map(operator => {
        return {
          ...operator,
          getValueAsString: (value: number | number[]) => {
            const values = Array.isArray(value) ? value : [value];
            return values.map(v => Object.values(executive_contact_tags)
              .find(e => e.id === v)?.name || 'None').join(', ');
          }
        };
      })
    },
    {
      field: 'contact_type',
      headerName: renderForExport ? "" : 'Contact Type',
      width: renderForExport ? 0 : 140,
      minWidth: 0,
      editable: false,
      filterable: filteringEnabled,
      sortable: filteringEnabled,
      display: "flex",
      renderCell: renderForExport ? undefined : renderType,
      headerAlign: 'left',
      filterOperators: selectFilterOperator([
        { options: executiveContactTypeOptions, rawOptions: contact_types }
      ]).map(operator => {
        return {
          ...operator,
          getValueAsString: (value: number) => {
            if (Array.isArray(value)) {
              return value.map(v => Object.values(contact_types)
                .find(e => e.id === v)?.name || 'None').join(', ');
            }
            return Object.values(contact_types).find(e => e.id === value)?.name || 'None';
          }
        };
      })
    },
  ], [executiveContactTagOptions, executiveContactTypeOptions, 
    executive_contact_tags, contact_types, filteringEnabled, 
    renderTags, renderContactName, renderType, renderForExport]);

  const initialState = useMemo(() => ({
    pagination: { 
      paginationModel: { 
        pageSize: rows && rows.length > MAX_PAGE_SIZE ? MAX_PAGE_SIZE : rows.length,
      }
    },
  }), [rows]);

  const dataRows = useMemo(() => ( 
    rows?.filter(row => executive_contacts[row.attendee.id]?.is_resource === false) ?? [] 
  ), [executive_contacts, rows]);

  const pageSizeOptions = useMemo(() => (
    rows?.length !== undefined ? [rows.length > MAX_PAGE_SIZE ? MAX_PAGE_SIZE : rows.length] : [0]
  ), [rows.length]);

  const slots = useMemo(() => ({
    noRowsOverlay: () => (
      <NoRowOverlay emptyMessage="No Contact Data Available" msTimeout={10000} />
    ),
    toolbar: filteringEnabled && !renderForExport 
      ? ContactGridToolbarSlot
      : ContactGridSpacerToolbarSlot
  }), [filteringEnabled, renderForExport]);

  const slotProps = useMemo<DataGridProProps["slotProps"]>(() => ({
    toolbar: {
      handleEditLabelModal
    } as unknown as NonNullable<DataGridProProps["slotProps"]>["toolbar"]
  }), [handleEditLabelModal]);

  return (
    <CabDataGrid
      initialState={initialState}
      rows={renderForExport ? dataRows.slice(0, MAX_EXPORT_ROWS) : dataRows}
      columns={columns}
      loading={loading}
      paginationMode="client"
      filterMode="server"
      sortingMode="server"
      pagination
      getRowId={(row) => row.attendee.id}
      pageSizeOptions={pageSizeOptions}
      onSortModelChange={handleSortModelChange}
      onFilterModelChange={handleFilterModelChange}
      hideFooter
      disableVirtualization
      showToolbar={!!slots?.toolbar}
      slots={slots}
      slotProps={slotProps}
    />
  );
};

export default ContactDataGrid;
